/**
 * ⌘K as the World view of the composer (RFC 0336 3c, S7).
 *
 * The compact composer grows upward into one conversation with the World
 * agent, with the launcher's rows beneath it. Not a centred overlay with a
 * backdrop: it sits where the composer strip sits, along the bottom, so it
 * reads as the same box opened out rather than a dialog over the work.
 *
 * ⌘K opens on the sessions list, not the thread. Typing turns it into
 * today's launcher: the same rows and ranking, with Ask pointed at the World
 * agent and New session second (`Launcher`'s `onAskWorld`). Picking Ask opens
 * the World agent's thread, and the view is a conversation from then on. ⌘.
 * opens straight onto the thread, with no list. Esc closes it; the World
 * agent's thread is a session like any other, so it is still there the next
 * time.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  type AttachmentRef,
  parseQuestionBody,
  projectSessionActiveRun,
  projectSessionPendingInteractions,
  projectSessionTranscript,
  registryStatus,
  type TranscriptMessage,
} from '@mod/core/model'
import { Maximize2 } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ComposerSlotProvider } from '../../features/thread/ComposerSlot'
import { OpenRef } from '../../features/thread/Markdown'
import { SessionLiveLine } from '../../features/thread/SessionActivity'
import { useStopTrailing } from '../../features/thread/stopVerdict'
import { subthreadState } from '../../features/thread/subthreadState'
import { ThreadRowView } from '../../features/thread/ThreadRowView'
import type { CardTarget } from '../../features/thread/threadCards'
import {
  buildThreadRows,
  type ThreadRow,
  toolResults,
} from '../../features/thread/threadRows'
import { SessionMark } from '../../services/sessions/SessionMark'
import { useDocument } from '../../services/world/repo/useDocument'
import type { WorldSessionRow } from '../../services/world/useWorldSessions'
import { Launcher, type LauncherComposerProps } from './Launcher'
import type { CommandTarget } from './launcherTargets'
import { WORLD_LINE_LIMIT } from './liveLines'
import { useWorldLines } from './useWorldLines'
import { WorldLines } from './WorldLines'
import { worldAgentName } from './worldAgentName'

/** Popups that belong to the view though they draw outside it. */
const OWNED_POPUPS =
  '[role="menu"],[role="listbox"],[role="dialog"],[data-radix-popper-content-wrapper]'

const NO_SESSIONS: readonly WorldSessionRow[] = []
/** How many scrolled-to or hovered lines are read beyond the first few. */
const WANT_CAP = 16

/**
 * A key the World view may take for its lines: pressed in the composer's own
 * box, or with nothing editable focused. Any other input, textarea or
 * editable element owns its keys.
 */
export function fromTheBox(
  target: EventTarget | null,
  section: Element | null,
): boolean {
  const el = target as HTMLElement | null
  if (!el || typeof el.closest !== 'function') return true
  const editable =
    el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
  if (!editable) return true
  return el === section?.querySelector('textarea')
}

/** How many rows of the World agent's thread the view shows. */
const THREAD_TAIL = 6

/**
 * The World agent's last few rows, drawn as its thread draws them in a pane
 * (`buildThreadRows`): what was said, the sessions it started or messaged, each
 * agent that stopped (RFC 0348 3e). One difference, for room: a run of plain
 * tool calls is left out, since six rows are the whole view. A call that
 * produced a card (a session started, a document written) stays.
 */
export function worldThreadRows(
  transcript: readonly TranscriptMessage[],
  results: ReturnType<typeof toolResults>,
  max = THREAD_TAIL,
): ThreadRow[] {
  return buildThreadRows([...transcript], (id) => results.get(id))
    .filter((row) => row.kind !== 'tools')
    .slice(-max)
}

/** The question Mod is waiting on, as one line with its options. */
export function pendingQuestionLine(sessionDoc: unknown): string | null {
  const pending = projectSessionPendingInteractions(sessionDoc).find(
    (i) => i.kind === 'question',
  )
  if (!pending) return null
  const q = parseQuestionBody(pending.bodyJson).questions[0]
  if (!q) return null
  const options = q.options.map((o) => o.label).join(' / ')
  return options ? `${q.prompt} (${options})` : q.prompt
}

export function WorldView({
  open,
  threadOnly = false,
  worldAgentSessionId,
  targets,
  onClose,
  onPickTarget,
  onOpenUrl,
  onAsk,
  onAskWorld,
  onRename,
  composer,
  sessions = NO_SESSIONS,
  onPutThrough,
  onFileDone,
  onExpand,
  onStop,
}: {
  open: boolean
  /** ⌘. (RFC 0336 S7): open straight onto the thread, for talking, with no
   *  launcher rows. ⌘K opens with rows until the World agent is asked. */
  threadOnly?: boolean
  /** The World agent's session, once the World has one. */
  worldAgentSessionId: string | null
  targets: CommandTarget[]
  onClose: () => void
  onPickTarget: (target: CommandTarget) => void
  onOpenUrl: (url: string) => void
  /** New session: start one where the chips say, with this text. */
  onAsk: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /** Ask the World agent. */
  onAskWorld: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /** Name the World agent: the header's name is editable in place. */
  onRename?: (name: string) => void
  composer?: LauncherComposerProps
  /** Every session in the World, off the registries (RFC 0336 S10): which
   *  get a live line between the thread and the box. */
  sessions?: readonly WorldSessionRow[]
  /** A line picked: put through to its session (RFC 0336 3d). */
  onPutThrough?: (sessionId: string) => void
  /** File a session as done (RFC 0286), from its line: ⌫ then ↵, or the
   *  check twice. Its line goes once the registry says it is filed. */
  onFileDone?: (sessionId: string, projectId: string) => void
  /** Open the World agent's thread in a pane, as the strip's expand does for
   *  any other session. Absent until the World has an agent session. */
  onExpand?: () => void
  /** Stop the World agent's turn. */
  onStop?: () => void
}) {
  const [sessionDoc] = useDocument(
    (worldAgentSessionId ?? undefined) as AnyDocumentId | undefined,
  )
  const name = worldAgentName(sessionDoc)
  const [renaming, setRenaming] = useState<string | null>(null)
  useEffect(() => {
    if (!open) setRenaming(null)
  }, [open])
  const transcript = useMemo(
    () => projectSessionTranscript(sessionDoc),
    [sessionDoc],
  )
  const results = useMemo(() => toolResults(transcript), [transcript])
  const thread = useMemo(
    () => worldThreadRows(transcript, results),
    [transcript, results],
  )
  const stopTrailing = useStopTrailing(sessionDoc)
  // What the World agent is doing, off its own log: it is in no project, so
  // no registry speaks for it the way one does for a session's strip.
  const activeRun = useMemo(
    () => projectSessionActiveRun(sessionDoc),
    [sessionDoc],
  )
  const runStatus = activeRun
    ? activeRun.claimed
      ? ('running' as const)
      : ('queued' as const)
    : undefined
  // Cards read by the names the sessions have now, and say what each is doing,
  // off the World's registries: the same reads the lines below use.
  const resolveTitle = useMemo(() => {
    const byId = new Map(sessions.map((s) => [s.id, s.title]))
    return (target: CardTarget, id: string) =>
      target === 'session' ? byId.get(id) : undefined
  }, [sessions])
  const resolveSessionState = useMemo(() => {
    const now = Date.now()
    const byId = new Map(sessions.map((s) => [s.id, s]))
    return (id: string) => {
      const row = byId.get(id)
      if (!row) return undefined
      const status = registryStatus(row, now)
      return subthreadState({
        ...(status ? { status } : {}),
        ...(row.completedAt != null ? { completedAt: row.completedAt } : {}),
        ...(row.sentAt != null ? { sentAt: row.sentAt } : {}),
      })
    }
  }, [sessions])

  // WHERE YOUR AGENTS ARE (RFC 0336 3f, S10): a line per session asking,
  // working or finished since you looked, read off the log, no turn asked.
  // Selected by session, not by place: answering a question moves its line
  // from asking to working, and the selection should go with it.
  const [lineId, setLineId] = useState<string | null>(null)
  // Lines scrolled to or hovered, read so they say something (bounded: a
  // window's worth at a time, replaced as you scroll).
  const [wanted, setWanted] = useState<readonly string[]>([])
  const { lines, seen } = useWorldLines(sessions, {
    worldAgentId: worldAgentSessionId,
    open,
    alsoRead: lineId ? [lineId, ...wanted] : wanted,
  })
  const lineIndex = lineId ? lines.findIndex((l) => l.sessionId === lineId) : -1
  const selectedLine = lineIndex === -1 ? null : lineIndex
  const draft = useRef('')
  // Something is typed: the lines step aside for it, and come back if the
  // box is cleared before sending (RFC 0336 3f).
  const [typing, setTyping] = useState(false)
  const numberKey = useRef<((n: number) => boolean) | null>(null)
  // A finished line is news once: closing the view after it SHOWED is having
  // seen it. Shown means on screen without scrolling (the bottom rows, by the
  // box) or walked to with ↑; one scrolled out of view stays news.
  const walkedTo = useRef<Set<string>>(new Set())
  if (lineId) walkedTo.current.add(lineId)
  const shownDone = useRef<string[]>([])
  shownDone.current = open
    ? lines
        .filter(
          (l, i) =>
            l.state === 'done' &&
            (i >= lines.length - WORLD_LINE_LIMIT ||
              walkedTo.current.has(l.sessionId)),
        )
        .map((l) => l.sessionId)
    : shownDone.current
  // FILE AS DONE asks first: ⌫ (or the check) names the line, ↵ (or the
  // check again) files it, anything else lets it go.
  const [confirming, setConfirming] = useState<string | null>(null)
  const confirmingRef = useRef(confirming)
  confirmingRef.current = confirming
  const fileDone = (sessionId: string) => {
    const line = lines.find((l) => l.sessionId === sessionId)
    setConfirming(null)
    if (!line || !onFileDone) return
    seen([sessionId])
    if (lineId === sessionId) setLineId(null)
    onFileDone(sessionId, line.projectId)
  }
  useEffect(() => {
    if (open) return
    setLineId(null)
    setConfirming(null)
    setWanted([])
    seen(shownDone.current)
    shownDone.current = []
    walkedTo.current = new Set()
  }, [open, seen])
  const putThrough = (sessionId: string) => {
    seen([sessionId])
    setLineId(null)
    onPutThrough?.(sessionId)
  }
  /** ↑ from the empty box walks the lines, ↓ walks back, Return goes there. */
  const onIdleKey = (key: 'up' | 'down' | 'tab' | 'enter'): boolean => {
    if (
      chatting ||
      threadOnly ||
      draft.current.trim() !== '' ||
      lines.length === 0
    )
      return false
    if (key === 'up' || key === 'down') setConfirming(null)
    if (key === 'up') {
      const at = selectedLine === null ? lines.length : selectedLine
      setLineId(lines[Math.max(0, at - 1)]?.sessionId ?? null)
      return true
    }
    if (selectedLine === null) return false
    if (key === 'down') {
      setLineId(lines[selectedLine + 1]?.sessionId ?? null)
      return true
    }
    if (key === 'enter') {
      const line = lines[selectedLine]
      if (line && confirming === line.sessionId) fileDone(line.sessionId)
      else if (line) putThrough(line.sessionId)
      return true
    }
    return false
  }
  // 1-9 answers the selected line's question, and ⌫ asks to file it as
  // done, before the box takes either key.
  const selectedId =
    selectedLine === null ? null : lines[selectedLine]?.sessionId
  useEffect(() => {
    if (!open || !selectedId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || draft.current.trim() !== '') return
      // Only from the box. A key typed into any other field (the name being
      // renamed, a menu's search) is that field's.
      if (!fromTheBox(e.target, sectionRef.current)) return
      if (e.key === 'Backspace' && onFileDone) {
        e.preventDefault()
        e.stopPropagation()
        setConfirming(selectedId)
        return
      }
      if (e.metaKey || !/^[1-9]$/.test(e.key)) return
      if (numberKey.current?.(Number(e.key))) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, selectedId, onFileDone])
  // A question Mod is waiting on, drawn under the thread: what you type next
  // answers it (WorldShell answers before it sends).
  const asking = useMemo(() => pendingQuestionLine(sessionDoc), [sessionDoc])

  // Pinned to the latest message, as every thread is, unless you have
  // scrolled up to read: then a new message does not pull you away.
  const threadRef = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  // Opening starts at the bottom, wherever it was left.
  useLayoutEffect(() => {
    if (!open) return
    pinned.current = true
    const el = threadRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [open])
  // biome-ignore lint/correctness/useExhaustiveDependencies: `thread` is the trigger; the effect reads the element
  useLayoutEffect(() => {
    const el = threadRef.current
    if (!el || !pinned.current) return
    el.scrollTop = el.scrollHeight
  }, [thread])

  // Opening puts the caret in the box, every time: the view stays mounted
  // while closed, so the composer's own autofocus fires only once, and ↑
  // into the lines needs the box to have the keys.
  useEffect(() => {
    if (!open) return
    const box = sectionRef.current?.querySelector('textarea')
    box?.focus()
  }, [open])

  // Once the World agent has been asked, the view is a conversation: what is
  // typed next is a reply, not a search, until the view closes.
  const [chatting, setChatting] = useState(false)
  useEffect(() => {
    if (!open) setChatting(false)
  }, [open])

  // Escape closes, in one press, whatever is typed: the same capture rule the
  // overlay had, so it lands before the composer's own Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Renaming: Escape cancels the name, not the view.
      if ((e.target as Element | null)?.closest?.('[data-world-rename]')) return
      e.preventDefault()
      e.stopPropagation()
      // Asked to file a line as done: Escape takes the ask back first.
      if (confirmingRef.current) {
        setConfirming(null)
        return
      }
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  // Pressing anywhere outside closes it too, and the press still does what it
  // was for: a tap on a document closes the view and lands in the document.
  // Menus the composer opens (the model, a project) draw outside the frame,
  // so a press in one is not outside.
  const sectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target || sectionRef.current?.contains(target)) return
      if (target.closest?.(OWNED_POPUPS)) return
      onClose()
    }
    window.addEventListener('pointerdown', onDown, true)
    return () => window.removeEventListener('pointerdown', onDown, true)
  }, [open, onClose])

  // Kept mounted while closed, so its draft survives (the shell hides it).

  // The thread is for talking: ⌘. opens onto it, and ⌘K only once the World
  // agent has been asked. Until then ⌘K is the sessions list and the search.
  const threadEl =
    (chatting || threadOnly) && (thread.length > 0 || asking) ? (
      <div
        ref={threadRef}
        onScroll={(e) => {
          const el = e.currentTarget
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24
        }}
        className="flex max-h-[34vh] flex-col gap-2 overflow-y-auto pb-2"
      >
        {/* Drawn as the World agent's thread draws them in a pane, so a
            card, a status or a screenshot reads the same in both. */}
        {thread.map((row) => (
          <div key={row.key} className="shrink-0">
            <ThreadRowView
              row={row}
              results={results}
              onOpenSession={putThrough}
              resolveTitle={resolveTitle}
              resolveSessionState={resolveSessionState}
              stopTrailing={stopTrailing}
            />
          </div>
        ))}
        {asking ? (
          <div className="px-2 py-1.5 text-[13.5px] text-foreground/90">
            {asking}
          </div>
        ) : worldAgentSessionId ? (
          <SessionLiveLine
            sessionId={worldAgentSessionId}
            sessionDoc={sessionDoc}
            status={runStatus}
          />
        ) : null}
      </div>
    ) : null
  // The lines step aside as soon as you start typing, and come back if the
  // box is cleared. Once you have asked the World agent something they stay
  // aside: this is a conversation now, until the view closes and opens again.
  // ⌘. never shows them: it goes straight to the thread.
  const linesEl =
    lines.length > 0 && !chatting && !threadOnly && !typing ? (
      <WorldLines
        lines={lines}
        selected={selectedLine}
        onPick={(line) => putThrough(line.sessionId)}
        numberKeyRef={numberKey}
        onWant={(ids) =>
          setWanted((prev) => {
            // Newest first, kept with what was read before so a line already
            // showing its text does not lose it on the next hover; capped.
            const next = [...new Set([...ids, ...prev])].slice(0, WANT_CAP)
            return prev.join(' ') === next.join(' ') ? prev : next
          })
        }
        confirming={confirming}
        onAskDone={(line) => setConfirming(line.sessionId)}
        {...(onFileDone ? { onDone: (line) => fileDone(line.sessionId) } : {})}
        live={open}
      />
    ) : null
  const aboveEl =
    threadEl || linesEl ? (
      <>
        {threadEl}
        {threadEl && linesEl ? (
          <div className="mx-2 mb-1.5 h-px bg-foreground/[0.08]" />
        ) : null}
        {linesEl}
      </>
    ) : null

  // THE STRIP, OPENED UPWARD (RFC 0336 S7): the same compact composer frame,
  // the same width and place, with the World line above the box and the
  // thread and rows inside the frame. Not a box laid over the strip.
  return (
    // A ref in the thread opens what it names, the way it does in a session.
    <OpenRef.Provider
      value={(id) => {
        const target = targets.find((t) => t.id === id)
        if (!target) return
        onPickTarget(target)
        onClose()
      }}
    >
      <ComposerSlotProvider value={{ variant: 'compact', element: null }}>
        <section
          ref={sectionRef}
          aria-label={`${name}, or type to jump`}
          className="mx-auto flex w-full max-w-[600px] flex-col items-start gap-1 px-3 pb-1"
        >
          <div className="w-full">
            <Launcher
              size="compact"
              targets={targets}
              inside={{
                header: (
                  <div className="flex w-full items-center gap-1.5 px-1 pb-1.5 text-xs text-muted-foreground">
                    {/* Its mark, as every session has one (RFC 0311). */}
                    {worldAgentSessionId ? (
                      <SessionMark id={worldAgentSessionId} size={14} />
                    ) : null}
                    {renaming !== null ? (
                      <input
                        data-world-rename
                        // biome-ignore lint/a11y/noAutofocus: opened by a click on the name, to type the new one
                        autoFocus
                        value={renaming}
                        aria-label="Name"
                        onChange={(e) => setRenaming(e.target.value)}
                        onBlur={() => setRenaming(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            onRename?.(renaming)
                            setRenaming(null)
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            setRenaming(null)
                          }
                        }}
                        className="min-w-0 rounded bg-foreground/[0.06] px-1 font-medium text-foreground outline-none"
                      />
                    ) : (
                      <button
                        type="button"
                        title={onRename ? 'Rename' : undefined}
                        disabled={!onRename}
                        onClick={() => setRenaming(name)}
                        className="font-medium text-foreground"
                      >
                        {name}
                      </button>
                    )}
                    <span className="flex-1" />
                    {onExpand ? (
                      <button
                        type="button"
                        onClick={onExpand}
                        aria-label={`Open ${name}'s thread`}
                        title={`Open ${name}'s thread`}
                        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <Maximize2 className="size-3" strokeWidth={2.25} />
                      </button>
                    ) : null}
                  </div>
                ),
                above: aboveEl,
              }}
              onPickTarget={(target) => {
                onPickTarget(target)
                onClose()
              }}
              onOpenUrl={(url) => {
                onOpenUrl(url)
                onClose()
              }}
              onAsk={async (text, model, skills, attachments) => {
                onClose()
                await onAsk(text, model, skills, attachments)
              }}
              // Stays open: the answer arrives in this thread.
              onAskWorld={async (text, model, skills, attachments) => {
                setChatting(true)
                setLineId(null)
                setConfirming(null)
                await onAskWorld(text, model, skills, attachments)
              }}
              chatting={chatting || threadOnly}
              worldName={name}
              // Talking to the World agent, the box says when it is
              // working, as a session's strip does: Stop beside Send.
              composer={
                (chatting || threadOnly) && activeRun
                  ? { ...composer, busy: true, ...(onStop ? { onStop } : {}) }
                  : composer
              }
              placeholder={
                chatting || threadOnly
                  ? thread.length > 0
                    ? `Reply to ${name}`
                    : `Talk to ${name}`
                  : `Ask ${name}, or type to jump`
              }
              listClassName="max-h-[40vh] overflow-y-auto pb-1"
              onIdleKey={onIdleKey}
              onQueryChange={(q) => {
                draft.current = q
                setTyping(q.trim() !== '')
                if (q.trim() !== '') setLineId(null)
              }}
            />
          </div>
        </section>
      </ComposerSlotProvider>
    </OpenRef.Provider>
  )
}
